//changed due to package upgrade
import React, { useEffect, useRef, useState } from 'react';
import AppsContainer from '@projectK/components/AppsContainer';
import {
  WaiverTable,
  AddWaivers,
} from '@projectK/modules/General/WaiverSettings';
import { useIntl } from 'react-intl';
import {
  alpha,
  Button,
  FormControl,
  useMediaQuery,
  useTheme,
    MenuItem,
  Select,
  Typography,
} from '@mui/material';

import AppsHeader from '@projectK/components/AppsHeader';
import AppsContent from '@projectK/components/AppsContent';
import AppsPagination from '@projectK/components/AppsPagination';
import Box from '@mui/material/Box';
import AppSearchBar from '@projectK/components/AppSearchBar';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import AppInfoView from '@projectK/components/AppInfoView';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { Fonts } from '@projectK/constants/AppEnums';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import {hasPermission,useRolePermissions} from '@projectK/context/RolesProvider';

const WaiverSettings = () => {
  const { rolePermissions } = useRolePermissions();
  const companyTheme = CompanyTheme();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const { verbiageSettings } = useApplicationSettings();
  const { messages } = useIntl();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const infoViewActionsContext = useInfoViewActionsContext();
  const [perPage, setPerPage] = useState(20);
  const [titleSorting, setTitleSorting] = useState(null);
  const [descriptionSorting, setDescriptionSorting] = useState(null);
  const [signatureSorting, setSignatureSorting] = useState(null);
  const timeoutRef = useRef(null); // Ref to store the timeout ID
  const abortControllerRef = useRef(null);
  const handleChangePerPage = (event) => {
    const newPerPage = event.target.value;
    setPerPage(newPerPage);
    setPage(0);
  };
  const onPageChange = (event, value) => {
    setPage(value);
  };
  useEffect(() => {
    fetchData();
   return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    search,
    page,
    perPage,
    titleSorting,
    descriptionSorting,
    signatureSorting,
  ]);

  const fetchData = async () => {
    try {
      setLoading(true);
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;
      const response = await jwtAxios.get(`/waiver-settings/`, {
        params: {
          search,
          page,
          perPage,
          sortings: {
            waiver_name: titleSorting,
            description: descriptionSorting,
            digital_signature: signatureSorting,
          },
        },
        signal,
      });
      const { data } = response;
      setList(data.list);
      setTotal(data.total);
      data.list.length === 0 && page > 0 && setPage(page - 1);
      setLoading(false);
    } catch (error) {
     if (error.message !== 'canceled') {
           console.error('An error occurred:', error);
           setLoading(false);
      }
    }
  };
  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  const onSearchOrder = (value) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setSearch(value);
      setPage(0);
    }, 500);
  };

  const handleDelete = (itemId) => {
    jwtAxios
      .delete(`/waiver-settings/${itemId}`)
      .then((response) => {
        if (Array.isArray(list)) {
          setList(list.filter((item) => item.id !== itemId));
        }
        fetchData();
        infoViewActionsContext.showMessage(
          'Waiver Settings deleted successfully!',
        );
      })
      .catch((error) => {
        infoViewActionsContext.fetchError('Error deleting Waiver Settings.');
        console.error('Error deleting Waiver Settings:', error);
      });
  };
  return (
    <>
      <Typography
        variant='h4'
        sx={{
          color: companyTheme ? companyTheme.primary.main : 'text.primary',
          fontWeight: Fonts.SEMI_BOLD,
        }}
      >
        {/* <IntlMessages id='sidebar.product.waiversettings' /> */}
        {`${verbiageSettings?.waiver_name || 'Waiver'} Settings`}
      </Typography>
      <div className='event-search ps-h'>
        <AppsContainer fullView>
          <AppsHeader>
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              width={1}
              justifyContent='space-between'
              className='main-box'
            >
              <Box display='flex' flexDirection='row' className='search-box'>
                <AppSearchBar
                  iconPosition='right'
                  overlap={false}
                  onChange={(event) => onSearchOrder(event.target.value)}
                  placeholder={messages['common.searchHere']}
                />
              </Box>
              <Box
                display='flex'
                flexDirection='row'
                alignItems='center'
                className='button-boxes'
              >
                 {hasPermission(rolePermissions, 'Company Settings - Waiver Settings', 'create') && (
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '&:hover': {
                      backgroundColor: companyTheme
                        ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                        : alpha('#2196f3', 0.8),
                    },
                  }}
                  onClick={openModal}
                >
                  Add {verbiageSettings?.waiver_name || 'Waiver'}
                </Button>
                 )}
                 {!isSmDown && (
                  <>                  <FormControl
                    variant='standard'
                    sx={{
                      borderRadius: 50,
                    }}
                    size='small'
                  >
                    <Select
                      value={perPage}
                      onChange={handleChangePerPage}
                      sx={{ marginLeft: '10px' }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={30}>30</MenuItem>
                      <MenuItem value={40}>40</MenuItem>
                      <MenuItem value={60}>60</MenuItem>
                      <MenuItem value={80}>80</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  <AppsPagination
                    rowsPerPage={perPage}
                    count={total}
                    page={page}
                    onPageChange={onPageChange}
                  />
                </>
                 )}
              </Box>
            </Box>
          </AppsHeader>

          <AppsContent
            className='evnt-s-table ps-events'
            sx={{
              paddingTop: 2.5,
              paddingBottom: 2.5,
            }}
          >
            <WaiverTable
              waivers={list}
              loading={loading}
              fetchData={fetchData}
              onDelete={handleDelete}
              verbiageSettings={verbiageSettings}
              titleSorting={titleSorting}
              setTitleSorting={setTitleSorting}
              descriptionSorting={descriptionSorting}
              setDescriptionSorting={setDescriptionSorting}
              signatureSorting={signatureSorting}
              setSignatureSorting={setSignatureSorting}
            />
          </AppsContent>

          {!isSmUp && (
            <>            <FormControl
              variant='standard'
              sx={{
                borderRadius: 50,
              }}
              size='small'
              className='cols-count colc25'
            >
              <Select
                value={perPage}
                onChange={handleChangePerPage}
                sx={{ marginLeft: '10px', height: '34px' }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={60}>60</MenuItem>
                <MenuItem value={80}>80</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <AppsPagination
              className='cols-pagination'
              rowsPerPage={perPage}
              count={total}
              page={page}
              onPageChange={onPageChange}
            />
          </>
          )}
          <AddWaivers
            isOpen={isModalOpen}
            onClose={closeModal}
            fetchData={fetchData}
            verbiageSettings={verbiageSettings}
          />
          <AppInfoView />
        </AppsContainer>
      </div>
    </>
  );
};

export default WaiverSettings;
